import { ChangeEvent, FC, FocusEventHandler, memo, useMemo, useState } from 'react'
import { PlanningClassification } from '@leadgen/models/client'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import OutlinedInput from '@mui/material/OutlinedInput'
import Typography from '@mui/material/Typography'
import { CheckboxButton } from '../../../atoms/CheckboxButton'
import useSearch from '../../../../hooks/shared/useSearch'
import { planningClassificationMap } from '../../../../constants/planningTaxonomy'
import ClassificationTile from './ClassificationTile'
import useStableCallback from '../../../../hooks/shared/useStableCallback'

type SearchablePlanningClassification = {
  planningClassification: PlanningClassification
  title: string
  description: string
}

const classificationSearchOptions = {
  keys: ['title', 'description'],
  threshold: 0.35,
  ignoreLocation: true,
}

const ClassificationGroup: FC<{
  title: string
  searchPlaceholder: string
  options: PlanningClassification[]
  selectedPlanningClassifications: Set<PlanningClassification>
  onChange: (planningClassification: PlanningClassification, checked: boolean) => void
  onSelectAllChange: (planningClassifications: PlanningClassification[], checked: boolean) => void
  onBlur?: FocusEventHandler<HTMLButtonElement>
}> = ({
  title,
  searchPlaceholder,
  options,
  selectedPlanningClassifications,
  onChange,
  onSelectAllChange,
  onBlur,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const searchableOptions = useMemo(
    () =>
      options.map(option => ({
        planningClassification: option,
        title: planningClassificationMap[option].title,
        description: planningClassificationMap[option].description,
      })),
    [options],
  )
  const visibleOptions = useSearch<SearchablePlanningClassification>({
    items: searchableOptions,
    searchTerm,
    options: classificationSearchOptions,
  }).map(option => option.planningClassification)
  const selectedCount = options.filter(option => selectedPlanningClassifications.has(option)).length
  const allSelected = selectedCount === options.length
  const onSelectAll = useStableCallback(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onSelectAllChange(options, checked)
    },
  )
  const onSearchTermChange = useStableCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  })

  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        padding: 2,
        border: theme => `1px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        backgroundColor: '#fff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
          }}
        >
          <CheckboxButton
            border={false}
            value={`select-all-${title}`}
            checked={allSelected}
            label="Select all"
            onChange={onSelectAll}
            variant="compact"
          />
          <Box
            sx={{
              minWidth: 24,
              height: 24,
              paddingX: 0.75,
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme => theme.palette.action.hover,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {selectedCount}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* <OutlinedInput
        size="small"
        value={searchTerm}
        onChange={onSearchTermChange}
        placeholder={searchPlaceholder}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        }
      /> */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {visibleOptions.map(option => (
          <ClassificationTile
            key={option}
            onBlur={onBlur}
            onChange={onChange}
            planningClassification={option}
            selected={selectedPlanningClassifications.has(option)}
          />
        ))}
      </Box>
      {!visibleOptions.length && (
        <Typography variant="body2" color="text.secondary">
          No matches in this group.
        </Typography>
      )}
    </Box>
  )
}

export default memo(ClassificationGroup)
